import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import TestimonialsSection from './TestimonialsSection';
import {
  Sparkles,
  Eye,
  Code,
  Palette,
  Star,
  Quote,
  Users,
  TrendingUp,
  Globe,
  Award,
  Heart,
  MessageCircle,
  ArrowRight,
  CheckCircle,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const TestimonialsDemo: React.FC = () => {
  const [selectedTestimonial, setSelectedTestimonial] = useState<any>(null);

  const handleViewAll = () => {
    console.log('View All clicked');
  };

  const handleShare = (testimonial: any) => {
    console.log('Share testimonial:', testimonial.name);
  };

  const handleContact = (testimonial: any) => {
    console.log('Contact testimonial:', testimonial.name);
  };

  const testimonials = [
    {
      id: '1',
      name: 'Sarah Chen',
      role: 'CFO',
      company: 'TechFlow Solutions',
      rating: 5,
      content: 'NeoBridge has revolutionized our international payment processes. We\'ve reduced transaction costs by 60% and processing time by 80%.',
      industry: 'Technology',
      verified: true,
      featured: true
    },
    {
      id: '2',
      name: 'Marcus Rodriguez',
      role: 'Head of Operations',
      company: 'Global Manufacturing Inc.',
      rating: 5,
      content: 'The compliance automation features are outstanding. We can now process payments to 40+ countries without worrying about regulatory issues.',
      industry: 'Manufacturing',
      verified: true,
      featured: true
    },
    {
      id: '3',
      name: 'Dr. Aisha Patel',
      role: 'Finance Director',
      company: 'MediCare Global',
      rating: 5,
      content: 'As a healthcare organization, we need the highest security standards. NeoBridge\'s blockchain technology provides the security we require.',
      industry: 'Healthcare',
      verified: true,
      featured: true
    }
  ];

  const keyStats = [
    { label: 'Customer Testimonials', value: '500+', icon: Users },
    { label: 'Average Rating', value: '4.9/5', icon: Star },
    { label: 'Industries Served', value: '15+', icon: Globe },
    { label: 'Success Rate', value: '98%', icon: TrendingUp }
  ];

  const features = [
    {
      icon: Quote,
      title: 'Real Customer Stories',
      description: 'Authentic testimonials from verified customers across industries'
    },
    {
      icon: Play,
      title: 'Interactive Carousel',
      description: 'Auto-playing carousel with manual controls and smooth transitions'
    },
    {
      icon: Star,
      title: 'Rating System',
      description: '5-star rating system with verified customer badges'
    },
    {
      icon: Users,
      title: 'Customer Diversity',
      description: 'Testimonials from various industries and company sizes'
    }
  ];

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-xl mb-4">
            <Sparkles className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">NeoBridge Testimonials</h1>
          <p className="text-gray-600">Customer success stories and feedback showcase</p>
        </div>

        {/* Key Stats */}
        <div className="grid gap-6 md:grid-cols-4 mb-8">
          {keyStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index}>
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mb-4">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Features */}
        <div className="grid gap-6 md:grid-cols-4 mb-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Icon className="h-5 w-5 mr-2" />
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Live Demo */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Eye className="h-5 w-5 mr-2" />
              Live Demo
            </CardTitle>
            <CardDescription>
              Interactive testimonials section with carousel and customer stories
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-gray-200 rounded-lg overflow-hidden">
              <TestimonialsSection
                onViewAll={handleViewAll}
                onShare={handleShare}
                onContact={handleContact}
              />
            </div>
          </CardContent>
        </Card>

        {/* Sample Testimonials */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Sample Testimonials</CardTitle>
            <CardDescription>Preview of customer feedback and ratings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-3">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="group cursor-pointer hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                        <CardDescription className="text-sm">
                          {testimonial.role} at {testimonial.company}
                        </CardDescription>
                        <div className="flex items-center space-x-2 mt-2">
                          <div className="flex">
                            {Array.from({ length: 5 }, (_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          {testimonial.verified && (
                            <Badge variant="secondary" className="text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Verified
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <blockquote className="text-gray-600 mb-4">
                      "{testimonial.content}"
                    </blockquote>
                    <div className="text-xs text-gray-500">
                      {testimonial.industry} • {testimonial.featured ? 'Featured' : 'Standard'}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Design Features */}
        <div className="grid gap-6 md:grid-cols-2 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Palette className="h-5 w-5 mr-2" />
                Design Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Auto-playing carousel with controls</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Customer avatars and verification badges</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Star ratings and industry tags</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Responsive grid layout</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Gradient backgrounds and animations</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Statistics overview section</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Code className="h-5 w-5 mr-2" />
                Technical Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">React hooks for state management</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">TypeScript interfaces and types</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">shadcn/ui components</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Radix UI Avatar component</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Event handling and callbacks</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">Responsive design patterns</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Ready to showcase customer success?
              </h3>
              <p className="text-gray-600 mb-4">
                The testimonials section is fully integrated and ready to use in your application.
              </p>
              <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                <ArrowRight className="h-4 w-4 mr-2" />
                View Implementation
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsDemo;
